/**
 * Audit log — spec §5-F19, §8.1 `/settings/audit`.
 *
 * Filters by actor, action, entity type and date range, with CSV export.
 * Retained 24 months by the TTL index; this screen is how you read it back.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, ScrollText } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { api, qs } from '@/lib/api';

interface AuditEntry {
  _id: string;
  actorId?: string;
  actorName: string;
  action: string;
  entityType: string;
  entityKey?: string;
  changes?: { field: string; from: unknown; to: unknown }[];
  metadata?: Record<string, unknown>;
  ip?: string;
  createdAt: string;
}

interface Facets {
  actions: string[];
  entityTypes: string[];
  actors: { _id: string; name: string; email: string }[];
}

interface Filters extends Record<string, unknown> {
  actorId?: string;
  action?: string;
  entityType?: string;
  from?: string;
  to?: string;
  q?: string;
}

/** Sensitive actions §F19 names explicitly — worth flagging in the list. */
const SENSITIVE = [
  'user.role_changed',
  'user.deactivated',
  'project.deleted',
  'task.deleted',
  'workspace.integrations_updated',
  'workspace.exported',
  'workspace.ownership_transferred',
  'auth.login_failed',
  'auth.account_locked',
  'leave.approved',
];

function useAudit(filters: Filters, page: number) {
  return useQuery({
    queryKey: ['audit', filters, page],
    queryFn: async () => {
      const res = await api.get<AuditEntry[]>(`/audit${qs({ ...filters, page, limit: 50 })}`);
      return { rows: res.data, total: res.meta?.total ?? res.data.length };
    },
  });
}

function useFacets() {
  return useQuery({
    queryKey: ['audit-facets'],
    queryFn: async () => (await api.get<Facets>('/audit/facets')).data,
  });
}

function describeChange(change: { field: string; from: unknown; to: unknown }): string {
  const show = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '∅';
    if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? '' : 's'}`;
    if (typeof v === 'object') return '…';
    return String(v).slice(0, 40);
  };
  return `${change.field}: ${show(change.from)} → ${show(change.to)}`;
}

export function AuditPage() {
  const [filters, setFilters] = useState<Filters>({});
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAudit(filters, page);
  const { data: facets } = useFacets();

  const set = (patch: Partial<Filters>): void => {
    setFilters((f) => {
      const next = { ...f, ...patch };
      for (const key of Object.keys(next) as (keyof Filters)[]) {
        if (!next[key]) delete next[key];
      }
      return next;
    });
    setPage(1);
  };

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Audit"
        subtitle="Every mutation, with who did it and what changed. Kept 24 months."
        actions={
          <a href={`/api/v1/audit/export${qs(filters)}`} download>
            <Button variant="ghost" size="sm">
              <Download />
              Export CSV
            </Button>
          </a>
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-7 w-48"
              placeholder="Actor or task key…"
              aria-label="Search the audit log"
              value={filters.q ?? ''}
              onChange={(e) => set({ q: e.target.value })}
            />
            <select
              aria-label="Actor"
              className="h-7 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-1.5 text-[12px]"
              value={filters.actorId ?? ''}
              onChange={(e) => set({ actorId: e.target.value })}
            >
              <option value="">Anyone</option>
              {(facets?.actors ?? []).map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Action"
              className="h-7 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-1.5 text-[12px]"
              value={filters.action ?? ''}
              onChange={(e) => set({ action: e.target.value })}
            >
              <option value="">Any action</option>
              {(facets?.actions ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <select
              aria-label="Entity type"
              className="h-7 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-1.5 text-[12px]"
              value={filters.entityType ?? ''}
              onChange={(e) => set({ entityType: e.target.value })}
            >
              <option value="">Any entity</option>
              {(facets?.entityTypes ?? []).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <Input
              type="date"
              className="h-7 w-36"
              aria-label="From date"
              value={filters.from ?? ''}
              onChange={(e) => set({ from: e.target.value })}
            />
            <Input
              type="date"
              className="h-7 w-36"
              aria-label="To date"
              value={filters.to ?? ''}
              onChange={(e) => set({ to: e.target.value })}
            />
            {Object.keys(filters).length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setFilters({})}>
                Clear
              </Button>
            )}
          </div>
        }
      />

      <div className="p-4">
        {isLoading ? (
          <Skeleton className="h-64" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ScrollText className="size-6" />}
            title="Nothing matches"
            description="Widen the date range or clear the filters."
          />
        ) : (
          <>
            <div className="vt-surface overflow-hidden" data-testid="audit-list">
              {rows.map((entry) => (
                <div
                  key={entry._id}
                  className="flex items-start gap-2 border-b border-[var(--border)] px-3 py-2 last:border-0"
                >
                  <span className="vt-mono w-36 shrink-0 text-[11px] vt-muted">
                    {new Date(entry.createdAt).toLocaleString()}
                  </span>
                  <span className="w-32 shrink-0 truncate text-[12px] font-medium">
                    {entry.actorName}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="vt-mono text-[12px]">{entry.action}</span>
                      {SENSITIVE.includes(entry.action) && <Badge tone="warn">sensitive</Badge>}
                      {entry.entityKey && (
                        <span className="vt-mono text-[11px] vt-muted">{entry.entityKey}</span>
                      )}
                    </div>
                    {entry.changes && entry.changes.length > 0 && (
                      <div className="truncate text-[11px] vt-muted">
                        {entry.changes.map(describeChange).join(' · ')}
                      </div>
                    )}
                  </div>
                  {entry.ip && (
                    <span className="vt-mono hidden shrink-0 text-[11px] vt-muted lg:inline">
                      {entry.ip}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-3 flex items-center gap-2 text-[12px] vt-muted">
              <span>
                {rows.length} of {total}
              </span>
              <div className="ml-auto flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page * 50 >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
